// Feature flags read from GET /api/health (WO Track B3 — FEATURE_COI deploys dark).
// The health endpoint is public and cheap; we cache it for ~5 minutes. While loading or on
// error the SAFE fallback is "COI disabled, mock engine" so the gated surface never flashes
// on for a deploy where the flag is off.
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import { qk } from "../lib/queryKeys.js";

export interface Features {
  coi: { enabled: boolean; engine: "mock" | "prophy" };
  /** WO-7/30 Track B: star-rating criteria guide. "" ⇒ no guide published; show the hint only. */
  ratingsGuideUrl?: string;
}

interface HealthResponse {
  ok: boolean;
  env: string;
  service: string;
  sha: string;
  nodeEnv: string;
  authMode: string;
  features: Features;
}

const SAFE_FALLBACK: Features = { coi: { enabled: false, engine: "mock" }, ratingsGuideUrl: "" };

export function useFeatures(): {
  features: Features;
  isLoading: boolean;
  /** True when the health fetch FAILED (after retries) — `features` is then a guess, not a
   *  server answer. Gated surfaces should offer a retry instead of silently redirecting. */
  isError: boolean;
  refetch: () => void;
} {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: qk.features,
    queryFn: () => api.get<HealthResponse>("/health"),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  return {
    features: data?.features ?? SAFE_FALLBACK,
    isLoading,
    isError,
    refetch: () => void refetch(),
  };
}
